// 'use client'; // falls du Next.js verwendest

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Papa from 'papaparse';
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import './wetter.css';
import '../pages/home.css';
import MyOrtMap from './MyOrtMap';
import { exportWeatherDataToPdf } from './functions'; // externe Funktion: empfängt logoDataUrl
import BfwLogo from '../assets/bfw_logo.png';




/*   UTILS IMPORT */
import { prettyStationName } from '../utils/prettyStationName';

const PDF = {
  marginPt: 36,
  logo: { maxW: 180, maxH: 58, gap: 18 },
  tableHead: { fillColor: [68, 118, 129], textColor: [255, 255, 255] }, // #447681
};

/* -------------------- Mapping UI-Keys -> CSV-Schlüssel (NORMALISIERT) -------------------- */
const paramMap = {
  min:           { label: 'Min Temp °C',              key: 'lt_minimum',                 color: '#4E79A7' },
  max:           { label: 'Max Temp °C',              key: 'lt_maximum',                 color: '#E15759' },
  avg:           { label: 'Ø Temp °C',                key: 'lt_mittel',                  color: '#59A14F' },

  humidity_avg:  { label: 'Ø Luftfeuchte [%]',       key: 'rf_mittel',                  color: '#EC7B09' },
  humidity_max:  { label: 'Max Luftfeuchte [%]',     key: 'rf_maximum',                 color: '#EFA357' },
  humidity_min:  { label: 'Min Luftfeuchte [%]',     key: 'rf_minimum',                 color: '#572E04' },

  wind_avg:      { label: 'Ø Wind [m/s]',            key: 'wg_mittel',                  color: '#15C9BA' },
  wind_max:      { label: 'Max Wind [m/s]',          key: 'wg_maximum',                 color: '#095952' },
  wind_min:      { label: 'Min Wind [m/s]',          key: 'wg_minimum',                 color: '#BFF6F1' },
  windrichtung:  { label: 'Windrichtung [°]',        key: 'wr_haeufigste_windrichtung', color: '#EDC948' },

  strahlung_avg: { label: 'Ø Strahlung [W/m²]',      key: 'gs_mittel',                  color: '#B07AA1' },
  strahlung_max: { label: 'Max Strahlung [W/m²]',    key: 'gs_maximum',                 color: '#9B0A72' },

  rain_sum:      { label: 'Niederschlag Summe [mm]', key: 'ns_summe',                   color: '#9dbcff' },
  rain_max:      { label: 'Niederschlag Max [mm]',   key: 'ns_maximum',                 color: '#1616f0' },
};

/* -------------------- Aliasse für CSV-Spaltennamen (NORMALISIERT) -------------------- */
const CSV_ALIASES = {
  lt_minimum: ['lt_minimum','tmin','ltmin','t_min'],
  lt_maximum: ['lt_maximum','tmax','ltmax','t_max'],
  lt_mittel:  ['lt_mittel','tmittel','tmean','t_avg','lt_mean'],

  rf_mittel:  ['rf_mittel','rh_mittel','lf_mittel','rh_mean'],
  rf_maximum: ['rf_maximum','rh_maximum','lf_maximum','rh_max'],
  rf_minimum: ['rf_minimum','rh_minimum','lf_minimum','rh_min'],

  wg_mittel:  ['wg_mittel','wind_mittel','ws_mean','wg_10min_mittel'],
  wg_maximum: ['wg_maximum','wind_max','ws_max','boe_max','gust_max'],
  wg_minimum: ['wg_minimum','wind_min','ws_min'],

  wr_haeufigste_windrichtung: [
    'wr_haeufigste_windrichtung','wr_haufigste_windrichtung','wr_haefigste_windrichtung',
    'windrichtung_haeufigste','wd_mode','winddir_mode'
  ],

  gs_mittel:  ['gs_mittel','globalstrahlung_mittel','strahlung_mittel','rs_global_mean'],
  gs_maximum: ['gs_maximum','globalstrahlung_max','rs_global_max'],

  ns_summe:   ['ns_summe','niederschlag_summe','rr_summe','niederschlag_mm','precip_sum'],
  ns_maximum: ['ns_maximum','niederschlag_max','rr_max','precip_max'],
};

/* -------------------- Gruppen -------------------- */
const GROUPS = {
  TEMPERATURE: ['min','max','avg'],
  HUMIDITY: ['humidity_min','humidity_max','humidity_avg'],
  WIND: ['wind_min','wind_max','wind_avg'],
  WINDDIR: ['windrichtung'],
  RADIATION: ['strahlung_avg','strahlung_max'],
  RAIN: ['rain_sum','rain_max'],
  ALL: Object.keys(paramMap),
};

/* -------------------- Helper (Normalisierung & Parsing) -------------------- */
const normalizeKey = (s) =>
  String(s ?? '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/[Ää]/g,'ae').replace(/[Öö]/g,'oe').replace(/[Üü]/g,'ue').replace(/ß/g,'ss')
    .toLowerCase()
    .replace(/\s+/g,'_');

const removeBOM = (s) => String(s ?? '').replace(/^\uFEFF/, '');

const toNumber = (v) => {
  if (v == null || v === '') return null;
  let s = String(v).trim().replace(/\u2212/g,'-').replace(/\s+/g,'');
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) s = s.replace(/\./g, '');
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) s = s.replace(/,/g, '');
  s = s.replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// robustes Datum parsen (ISO oder dd.mm.yyyy)
const parseDate = (d) => {
  if (!d) return null;
  const s = String(d).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s);
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (m) {
    const dd = m[1].padStart(2,'0');
    const mm = m[2].padStart(2,'0');
    const yyyy = m[3].length === 2 ? `20${m[3]}` : m[3];
    return new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  }
  const t = new Date(s);
  return Number.isNaN(+t) ? null : t;
};

const formatAT = (d) => (d ? new Date(d).toLocaleDateString('de-AT') : '');
const nf = new Intl.NumberFormat('de-AT', { maximumFractionDigits: 2 });


/* -------------------- Mobile Helpers -------------------- */
function useIsMobile(breakpointPx = 768) {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < breakpointPx : false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth < breakpointPx);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpointPx]);
  return isMobile;
}

/* ----- Bilder laden als DataURL fürs PDF ----- */
async function loadImageAsDataUrl(src) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0);
  return c.toDataURL('image/png');
}

/* ----- Proportionale Skalierung (kein Verzerren) ----- */
function fitContain(imgW, imgH, maxW, maxH) {
  const ratio = imgW / imgH;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  return { w, h };
}

/* ======= DOM-Snapshot so, dass PDF auf Mobile wie Desktop aussieht ======= */
const snapshotNodeToCanvas = async (node, desktopWidthPx = 1200) => {
  if (!node) return null;
  const clone = node.cloneNode(true);
  Object.assign(clone.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0px',
    width: `${desktopWidthPx}px`,
    height: 'auto',
    pointerEvents: 'none',
    opacity: '1',
    transform: 'none',
    zIndex: '-1'
  });
  document.body.appendChild(clone);
  await new Promise((r) => requestAnimationFrame(r));
  const canvas = await html2canvas(clone, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: desktopWidthPx,
  });
  document.body.removeChild(clone);
  return canvas;
};

const addDomImageMobileSafe = async ({
  pdf, node, label, nextYRef, pageW, pageH, margin, contentW,
  desktopWidthPx = 1200, widthFactor = 0.9,
}) => {
  if (!node) return;
  const canvas = await snapshotNodeToCanvas(node, desktopWidthPx);
  if (!canvas) return;
  const imgW = contentW * widthFactor;
  const naturalH = (canvas.height * imgW) / canvas.width;
  const availH = pageH - nextYRef.value - margin;
  if (naturalH > availH) { pdf.addPage(); nextYRef.value = margin; }
  const finalH = Math.min(naturalH, pageH - nextYRef.value - margin);
  const xCenter = margin + (contentW - imgW) / 2;
  if (label) {
    pdf.setFontSize(12);
    pdf.text(label, xCenter, nextYRef.value - 6);
  }
  pdf.addImage(canvas.toDataURL('./image/png'), 'PNG', xCenter, nextYRef.value, imgW, finalH);
  nextYRef.value += finalH + 18;
};
/* ========================================================================================= */

const Wetter = ({ ort }) => {
  const [rawData, setRawData] = useState([]);
  const [selectedParams, setSelectedParams] = useState(GROUPS.TEMPERATURE);
  const [pageIndex, setPageIndex] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [usedUrl, setUsedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const abortRef = useRef(null);

  const isMobile = useIsMobile(768);
  const [chartRange, setChartRange] = useState('auto');
  useEffect(() => { setChartRange(isMobile ? '30' : 'ALL'); }, [isMobile]);

  // Refs für Report-Screenshots
  const mapRef = useRef(null);
  const chartRef = useRef(null);

  // Dateinamen-Varianten
  const filenameVariants = useCallback((ortRaw) => {
    const base = String(ortRaw || '').trim();
    if (!base) return [];
    const norm = base
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[Ää]/g, 'ae').replace(/[Öö]/g, 'oe').replace(/[Üü]/g, 'ue').replace(/ß/g, 'ss')
      .trim();
    const lower = norm.toLowerCase();
    return Array.from(new Set([
      `${lower}.csv`,
      `${lower.replace(/\s+/g, '')}.csv`,
      `${lower.replace(/\s+/g, '-')}.csv`,
      `${lower.replace(/\s+/g, '_')}.csv`,
      `${norm.replace(/\b\w/g, m => m.toUpperCase()).replace(/\s+/g, '')}.csv`,
    ]));
  }, []);

  const buildCandidates = useCallback(() => {
    const base = 'https://geo.bfw.ac.at/downloads/waldoekodaten/wetter';
    const variants = filenameVariants(ort);
    return variants.map(v => `${base}/${v}`);
  }, [ort, filenameVariants]);

  const resolveByAliases = (row, canonicalKey) => {
    const aliases = CSV_ALIASES[canonicalKey] || [canonicalKey];
    for (const name of aliases) {
      if (row[name] != null && row[name] !== '') return row[name];
    }
    return null;
  };

  // CSV laden
  useEffect(() => {
    const tryFetch = async (url, signal) => {
      const res = await fetch(url, { mode: 'cors', cache: 'no-store', credentials: 'omit', signal });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      return removeBOM(await res.text());
    };

    const load = async () => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      setErrorMsg('');
      setUsedUrl('');
      setPageIndex(0);

      try {
        const candidates = buildCandidates();
        let csvText = null;
        let urlUsed = '';
        let lastErr = null;

        for (const url of candidates) {
          try { csvText = await tryFetch(url, ac.signal); urlUsed = url; break; }
          catch (e) { lastErr = e; }
        }
        if (!csvText) throw lastErr || new Error('CSV nicht gefunden');

        const result = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => normalizeKey(h),
        });

        const rows = (result.data || []).map((r) => {
          const obj = {};
          for (const k of Object.keys(r)) obj[normalizeKey(k)] = r[k];
          return obj;
        });

        const cleaned = rows
          .map((row) => {
            const dateKey = Object.keys(row).find((k) =>
              ['datum','date','datetime','zeitpunkt','messzeit','time','timestamp'].includes(k)
            );
            const parsed = parseDate(row[dateKey]);
            if (!parsed) return null;

            const out = { datum: parsed.toISOString().slice(0,10) };
            for (const [uiKey, { key: csvKey }] of Object.entries(paramMap)) {
              const raw = resolveByAliases(row, normalizeKey(csvKey));
              out[uiKey] = toNumber(raw);
            }
            return out;
          })
          .filter(Boolean)
          .sort((a,b) => parseDate(a.datum) - parseDate(b.datum));

        setRawData(cleaned);
        setUsedUrl(urlUsed);
      } catch (err) {
        console.error('Fehler beim CSV-Import:', err);
        setErrorMsg(String(err?.message || err));
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => abortRef.current?.abort();
  }, [buildCandidates]);

  /* -------------------- Paging (Tabelle) -------------------- */
  const entriesPerPageSafe = entriesPerPage === 'ALL' ? rawData.length : Number(entriesPerPage);
  const pageCount = useMemo(
    () => (entriesPerPageSafe > 0 ? Math.ceil(rawData.length / entriesPerPageSafe) : 0),
    [rawData.length, entriesPerPageSafe]
  );
  const displayedData = useMemo(() => {
    const start = pageIndex * entriesPerPageSafe;
    return rawData.slice(start, start + entriesPerPageSafe);
  }, [rawData, pageIndex, entriesPerPageSafe]);

  /* -------------------- UI Helpers -------------------- */
  const isActive = (keys) => {
    if (keys.length !== selectedParams.length) return false;
    const a = new Set(keys);
    return selectedParams.every((k) => a.has(k));
  };



  /* -------------------- PDF Download (Logo → Metadaten → Karte → Diagramm → Tabelle) -------------------- */
  const handleWeatherReportPdf = useCallback(async () => {
    if (!rawData.length) return;

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = PDF.marginPt;
    const contentW = pageW - margin * 2;

    // Logo & Titel
    let titleTop = margin;
    try {
      const logoDataUrl = await loadImageAsDataUrl(BfwLogo);
      const props = pdf.getImageProperties(logoDataUrl);
      const { w, h } = fitContain(props.width, props.height, 180, 58);
      const x = margin, y = margin - 2;
      pdf.addImage(logoDataUrl, 'PNG', x, y, w, h);
      titleTop = y + h + 18; // Abstand unter dem Logo
    } catch { titleTop = margin; }

    // (1) Metadaten
    pdf.setFontSize(16);
    pdf.text(`Wetter – ${prettyStationName(ort)}`, margin, titleTop);
    pdf.setFontSize(11);
    const paramLabels = selectedParams.map(k => paramMap[k]?.label || k).join(', ');
    const metaBits = [`Parameter: ${paramLabels || '—'}`].filter(Boolean);
    pdf.text(metaBits.join('  ·  '), margin, titleTop + 18);

    const nextYRef = { value: titleTop + 36 };

    // (2) Karte
    await addDomImageMobileSafe({
      pdf, node: mapRef.current, label: '',
      nextYRef, pageW, pageH, margin, contentW, desktopWidthPx: 1200, widthFactor: 1.15,
    });

    // (3) Diagramm
    await addDomImageMobileSafe({
      pdf, node: chartRef.current, label: 'Grafische Darstellung – Diagramm',
      nextYRef, pageW, pageH, margin, contentW, desktopWidthPx: 1200, widthFactor: 0.95,
    });

    // (4) Tabelle NUR mit ausgewählten Parametern (+ Datum)
    const headers = ['datum', ...selectedParams];
    const columns = headers.map(h => ({
      header: h === 'datum' ? 'Datum' : (paramMap[h]?.label || h),
      dataKey: h,
    }));

    const tableRows = rawData.map(r => {
      const o = { datum: formatAT(r.datum) };
      selectedParams.forEach(k => { o[k] = (r[k] == null ? '' : r[k]); });
      return o;
    });

    // Neue Seite, wenn knapp
    if (nextYRef.value > pageH - 120) {
      pdf.addPage();
      // optional: Logo auch auf Folgeseiten
      try {
        const logoDataUrl = await loadImageAsDataUrl(BfwLogo);
        const props = pdf.getImageProperties(logoDataUrl);
        const { w, h } = fitContain(props.width, props.height, 120, 36);
        pdf.addImage(logoDataUrl, 'PNG', margin, margin - 2, w, h);
        nextYRef.value = margin - 2 + h + 18;
      } catch {
        nextYRef.value = margin;
      }
    }

    pdf.autoTable({
      columns,
      body: tableRows,
      startY: nextYRef.value,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak', halign: 'left' },
      headStyles: {
  fillColor: [68, 118, 129],   // #447681
  textColor: [255, 255, 255], // weiß
  fontStyle: 'bold',
  halign: 'left'
},
      columnStyles: { datum: { halign: 'left', cellWidth: 60 } },
      margin: { left: margin, right: margin },
      didParseCell: function (data) {
        if (data.column.dataKey !== 'datum' && data.cell.raw !== '' && data.cell.raw != null) {
          const num = Number(data.cell.raw);
          if (!Number.isNaN(num)) data.cell.text = [nf.format(num)];
        }
      },
      didDrawPage: function () {
        const pageCount = pdf.internal.getNumberOfPages();
        const { pageNumber } = pdf.internal.getCurrentPageInfo();
        pdf.setFontSize(9);
        pdf.text(`Seite ${pageNumber} / ${pageCount}`, pageW - margin - 90, pageH - margin / 2);
      },
    });

    pdf.save(`Wetter_Report_${ort}.pdf`);
  }, [rawData, selectedParams, ort]);

  /* -------------------- CSV -> PDF: ALLE Daten & Parameter (mit Logo via neue Funktion) -------------------- */
  const handleCsvToPdf = useCallback(async () => {
    if (!rawData.length) return;
    let logoDataUrl = null;
    try { logoDataUrl = await loadImageAsDataUrl(BfwLogo); } catch {}
    exportWeatherDataToPdf({
      rawData,
      allKeys: GROUPS.ALL,
      getLabel: (key) => paramMap[key]?.label || key,
      formatDate: formatAT,
      stationName: prettyStationName(ort),
      filename: `Wetterdaten_alle_${ort}.pdf`,
      logoDataUrl, // << Logo an die Exportfunktion übergeben
    });
  }, [rawData, ort]);

  /* -------------------- Grafikdaten -------------------- */
  const chartData = useMemo(() => {
    if (!rawData.length) return [];
    if (chartRange === 'ALL') return rawData;
    const maxDays = chartRange === 'auto' ? (isMobile ? 30 : rawData.length) : Number(chartRange);
    if (!maxDays || maxDays >= rawData.length) return rawData;
    return rawData.slice(-maxDays);
  }, [rawData, chartRange, isMobile]);

  const chartHeight = isMobile ? 260 : 260;
  const xTickFontSize = isMobile ? 12 : 12;
  const yTickFontSize = isMobile ? 12 : 12;
  const xTickAngle = isMobile ? 0 : 0;
  const legendLayout = 'horizontal';
  const legendVerticalAlign = isMobile ? 'bottom' : 'top';
  const legendAlign = isMobile ? 'center' : 'right';
  const barSize = isMobile ? 16 : 16;
  const marginChart = isMobile ? { top: 5, right: 10, left: 0, bottom: 30 } : { top: 10, right: 20, left: 10, bottom: 10 };
  const perPointPx = isMobile ? 18 : 18;
  const minInnerWidth = Math.max(320, chartData.length * perPointPx);

  /* -------------------- Tabelle -------------------- */
  const tableHeaders = useMemo(() => ([
    { key: '__nr', label: 'Nr.' },
    { key: 'datum', label: 'Datum' },
    ...selectedParams.map(k => ({ key: k, label: paramMap[k]?.label || k }))
  ]), [selectedParams]);

  const formatCell = (key, value) => {
    if (key === 'datum') return formatAT(value);
    if (key === '__nr') return value;
    const num = typeof value === 'number' ? value : (value == null || value === '-' ? null : Number(value));
    return num == null || Number.isNaN(num) ? '-' : nf.format(num);
  };

  /* -------------------------------------- RENDER -------------------------------------- */
  return (
    <div className="wetter-container" id="pdfContent">
      <div ref={mapRef} className="home-map">
        <h2><strong>{prettyStationName(ort)}</strong> </h2>
        <MyOrtMap ort={ort} />
      </div>

      {/* Beschreibung */}
      <div className="home-text">
        <p>
          Die Wetterstation <strong>{prettyStationName(ort)}</strong> liefert täglich
          aktualisierte Daten zu Temperatur, Luftfeuchte, Wind, Strahlung und Niederschlag.
        </p>
      </div>
      <br />

      {/* Parameter-Buttons */}
      <div className="button-container">
        <button className={`param-button ${isActive(GROUPS.TEMPERATURE) ? 'active-button' : ''}`}
                onClick={() => { setSelectedParams(GROUPS.TEMPERATURE); setPageIndex(0); }}>
          Lufttemperatur
        </button>
        <button className={`param-button ${isActive(GROUPS.HUMIDITY) ? 'active-button' : ''}`}
                onClick={() => { setSelectedParams(GROUPS.HUMIDITY); setPageIndex(0); }}>
          Luftfeuchte
        </button>
        <button className={`param-button ${isActive(GROUPS.WIND) ? 'active-button' : ''}`}
                onClick={() => { setSelectedParams(GROUPS.WIND); setPageIndex(0); }}>
          Windgeschwindigkeit
        </button>
        <button className={`param-button ${isActive(GROUPS.WINDDIR) ? 'active-button' : ''}`}
                onClick={() => { setSelectedParams(GROUPS.WINDDIR); setPageIndex(0); }}>
          Windrichtung
        </button>
        <button className={`param-button ${isActive(GROUPS.RADIATION) ? 'active-button' : ''}`}
                onClick={() => { setSelectedParams(GROUPS.RADIATION); setPageIndex(0); }}>
          Globalstrahlung
        </button>
        <button className={`param-button ${isActive(GROUPS.RAIN) ? 'active-button' : ''}`}
                onClick={() => { setSelectedParams(GROUPS.RAIN); setPageIndex(0); }}>
          Niederschlag
        </button>
      </div>

      {/* Zeitraum-Selector (nur Grafik) */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        <label><strong>Zeitraum (Grafik):</strong></label>
        <select
          value={chartRange}
          onChange={(e) => setChartRange(e.target.value)}
          style={{ padding: '6px 10px' }}
          aria-label="Zeitraum für die Grafik wählen"
        >
          <option value="7">Letzte 7 Tage</option>
          <option value="14">Letzte 14 Tage</option>
          <option value="30">Letzte 30 Tage</option>
          <option value="ALL">Alle</option>
        </select>
      </div>

      {/* Diagramm */}
      <br />
      <h3>Grafische Darstellung</h3>
      <div ref={chartRef} className="linechart-outer" style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 8 }}>
        <div style={{ width: '100%', minWidth: minInnerWidth, height: chartHeight }}>
          <ResponsiveContainer>
            {isActive(GROUPS.RAIN) ? (
              <BarChart data={chartData} margin={marginChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="datum" type="category" tick={{ fontSize: xTickFontSize }} tickFormatter={formatAT}
                       angle={xTickAngle} textAnchor={isMobile ? 'end' : 'middle'} interval="preserveStartEnd"
                       minTickGap={isMobile ? 12 : 5} allowDuplicatedCategory={false}/>
                <YAxis tick={{ fontSize: yTickFontSize }} />
                <Tooltip labelFormatter={formatAT} />
                <Legend layout={legendLayout} verticalAlign={legendVerticalAlign} align={legendAlign}
                        wrapperStyle={{ paddingTop: isMobile ? 8 : 0, fontSize: isMobile ? 11 : 12 }}/>
                {GROUPS.RAIN.map((key) => (
                  <Bar key={key} dataKey={key} barSize={barSize} fill={paramMap[key]?.color || '#000'}
                       name={paramMap[key]?.label || key} isAnimationActive={!isMobile}/>
                ))}
              </BarChart>
            ) : (
              <LineChart data={chartData} margin={marginChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="datum" type="category" tick={{ fontSize: xTickFontSize }} tickFormatter={formatAT}
                       angle={xTickAngle} textAnchor={isMobile ? 'end' : 'middle'} interval="preserveStartEnd"
                       minTickGap={isMobile ? 12 : 5} allowDuplicatedCategory={false}/>
                <YAxis tick={{ fontSize: yTickFontSize }} />
                <Tooltip labelFormatter={formatAT} />
                <Legend layout={legendLayout} verticalAlign={legendVerticalAlign} align={legendAlign}
                        wrapperStyle={{ paddingTop: isMobile ? 8 : 0, fontSize: isMobile ? 11 : 12 }}/>
                {selectedParams.map((key) => (
                  <Line key={key} type="monotone" dataKey={key} stroke={paramMap[key]?.color || '#000'}
                        name={paramMap[key]?.label || key} dot={false} isAnimationActive={!isMobile}
                        connectNulls strokeWidth={isMobile ? 1 : 2}/>
                ))}
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      <br />
      <div>
        {usedUrl && (
          <a href={usedUrl} target="_blank" rel="noreferrer" className="param-button active-button" style={{ marginBottom: 0, marginRight: 8, textDecoration: 'none' }}>
            CSV Download
          </a>
        )}

        {/* CSV → PDF: ALLE Daten (mit Logo) */}
        <button onClick={handleCsvToPdf} className="param-button active-button" disabled={loading || !rawData.length} style={{ marginBottom: 0, marginRight: 8  }}>
          CSV → PDF Download
        </button>
      </div>

      {/* Messwerte */}
      <h3 style={{ marginTop: '3rem' }}>Messwerte</h3>

      <div className="entries-dropdown">
        <label><strong>Einträge pro Seite:</strong>{' '}</label>
        <select
          value={entriesPerPage === rawData.length ? 'ALL' : String(entriesPerPage)}
          onChange={(e) => {
            const val = e.target.value === 'ALL' ? rawData.length : parseInt(e.target.value, 10);
            setEntriesPerPage(val);
            setPageIndex(0);
          }}
          aria-label="Einträge pro Seite wählen"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value="ALL">ALLE</option>
        </select>
      </div>

      {/* Tabelle */}
      <div className="table-wrapper">
        <table className="data-table responsive-table" role="table" aria-label="Messwerte Tabelle">
          <thead>
            <tr>
              {tableHeaders.map(h => (
                <th key={h.key} scope="col" className={h.key === '__nr' || h.key === 'datum' ? '' : 'is-num'}>
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedData.map((row, idx) => {
              const nr = pageIndex * (entriesPerPage === 'ALL' ? rawData.length : entriesPerPage) + idx + 1;
              return (
                <tr key={idx}>
                  {tableHeaders.map((h) => {
                    const rawVal =
                      h.key === '__nr' ? nr :
                      h.key === 'datum' ? row.datum :
                      row[h.key];

                    const val = formatCell(h.key, rawVal);
                    const isNum = h.key !== '__nr' && h.key !== 'datum';

                    return (
                      <td
                        key={h.key}
                        data-label={h.label}
                        className={isNum ? 'is-num' : ''}
                      >
                        {val}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="page-buttons">
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPageIndex(i)}
              className={pageIndex === i ? 'active' : ''}
              aria-label={`Seite ${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      <br/>
      {/* Report-PDF */}
      <button onClick={handleWeatherReportPdf} className="param-button" disabled={loading || !rawData.length} style={{ marginBottom: 0 }}>
        PDF Download
      </button>
      <br /><br />
      <p><small>* „PDF Download“  enthält nur die aktuell gewählten Parameter.  ** „CSV → PDF Download“ exportiert die vollständigen Daten.</small></p>
    </div>
  );
};

export default Wetter;
